/**
 * Chapter-level "Generate PPTX for all lessons" dialog.
 *
 * The admin clicks "PPTX for all lessons" on a chapter row to fan out
 * AI-driven PowerPoint generation across every lesson in that chapter.
 * Each lesson's job is registered with the global `AiJobsContext` tray so
 * the admin can close this dialog (or navigate away) and the work keeps
 * progressing — exactly like the existing "Storyboards & videos" bulk
 * flow, but producing downloadable .pptx files instead of MP4 videos.
 *
 * On completion, every lesson gets a `pptx_url` attached and a Download
 * link appears under each lesson on the student Learn page.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAiJobs } from "@/lib/ai-jobs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
    Presentation, Sparkles, CheckCircle2, AlertCircle, Loader2, Download,
} from "lucide-react";
import { toast } from "sonner";

function statusFor(lessonId, trayJobs, lesson) {
    const job = Object.values(trayJobs || {}).find(
        (j) => j.kind === "pptx" && j.lessonId === lessonId
            && (j.status === "running" || j.status === "queued"),
    );
    if (job) return { kind: "running", job };
    if (lesson?.pptx_url) return { kind: "ready" };
    return { kind: "idle" };
}

export default function RenderAllPptxDialog({
    open,
    onOpenChange,
    chapterId,
    chapterTitle,
    lessons: lessonsProp = [],
    onAnyGenerated,
}) {
    const { trackJob, jobs: trayJobs } = useAiJobs();
    const [lessons, setLessons] = useState(lessonsProp);
    const [busy, setBusy] = useState(false);

    // Sync down whenever the parent re-renders us with fresh data
    useEffect(() => {
        setLessons(lessonsProp || []);
    }, [lessonsProp]);

    // Refresh from server when any pptx job for one of our lessons completes,
    // so the "Ready" pill / Download link appears without a manual reload.
    const refetchLessons = async () => {
        if (!chapterId) return;
        try {
            const ids = (lessonsProp || []).map((l) => l.id);
            const updated = await Promise.all(
                ids.map((id) => api.get(`/lessons/${id}`).then((r) => r.data).catch(() => null)),
            );
            setLessons(updated.filter(Boolean));
        } catch { /* best-effort */ }
    };

    useEffect(() => {
        const handler = (ev) => {
            const lid = ev?.detail?.lessonId;
            if (lid && lessonsProp.some((l) => l.id === lid)) {
                refetchLessons();
                onAnyGenerated?.();
            }
        };
        window.addEventListener("cco:lesson-pptx-generated", handler);
        return () => window.removeEventListener("cco:lesson-pptx-generated", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lessonsProp, chapterId]);

    const generatable = useMemo(
        () => lessons.filter((l) => (l.content || "").trim().length > 0),
        [lessons],
    );
    const missingContent = lessons.length - generatable.length;

    const startAll = async () => {
        if (!chapterId || !generatable.length) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/chapters/${chapterId}/pptx/generate-all`);
            (data.jobs || []).forEach((j) => {
                trackJob({
                    kind: "pptx",
                    jobId: j.job_id,
                    lessonId: j.lesson_id,
                    lessonTitle: j.lesson_title,
                    chapterTitle: data.chapter_title || chapterTitle || "Chapter",
                    total: 10,
                    onDone: (jobData) => {
                        try {
                            window.dispatchEvent(new CustomEvent("cco:lesson-pptx-generated", {
                                detail: { lessonId: j.lesson_id, pptxUrl: jobData?.pptx_url },
                            }));
                        } catch { /* no-op */ }
                        onAnyGenerated?.();
                    },
                });
            });
            toast.success(
                `Queued ${data.queued} deck${data.queued === 1 ? "" : "s"} for "${data.chapter_title}". `
                + `${data.skipped ? `Skipped ${data.skipped} empty lesson${data.skipped === 1 ? "" : "s"}.` : "Watch the AI jobs tray for progress."}`,
            );
            // Optimistic refresh so the rows immediately flip to "running".
            await refetchLessons();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not queue PPTX generation.");
        } finally {
            setBusy(false);
        }
    };

    const genOne = async (lesson) => {
        try {
            const { data } = await api.post(`/lessons/${lesson.id}/pptx/generate`);
            trackJob({
                kind: "pptx",
                jobId: data.job_id,
                lessonId: lesson.id,
                lessonTitle: data.lesson_title || lesson.title,
                chapterTitle: data.chapter_title || chapterTitle || "Chapter",
                total: 10,
                onDone: (jobData) => {
                    try {
                        window.dispatchEvent(new CustomEvent("cco:lesson-pptx-generated", {
                            detail: { lessonId: lesson.id, pptxUrl: jobData?.pptx_url },
                        }));
                    } catch { /* no-op */ }
                    onAnyGenerated?.();
                },
            });
            await refetchLessons();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start PPTX.");
        }
    };

    const runningCount = lessons.filter((l) => statusFor(l.id, trayJobs, l).kind === "running").length;
    const readyCount = lessons.filter((l) => l.pptx_url).length;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl"
                data-testid="render-all-pptx-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Presentation className="h-5 w-5 text-primary" />
                        PowerPoint decks — {chapterTitle || "Chapter"}
                    </DialogTitle>
                    <DialogDescription>
                        Generate a branded Cambridge .pptx for every lesson in this chapter.
                        Each deck gets 8-12 slides with speaker notes and stays attached to
                        the lesson for students to download.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5 flex items-center justify-between gap-3">
                        <div className="text-sm">
                            <div className="font-semibold">
                                {lessons.length} lesson{lessons.length === 1 ? "" : "s"} ·{" "}
                                <span className="text-primary">{readyCount} ready</span>
                                {runningCount ? (
                                    <span className="text-amber-700">
                                        {" · "}
                                        {runningCount} running
                                    </span>
                                ) : null}
                            </div>
                            {missingContent ? (
                                <div className="text-xs text-muted-foreground">
                                    {missingContent} lesson{missingContent === 1 ? "" : "s"} skipped — no content yet.
                                </div>
                            ) : null}
                        </div>
                        <Button
                            onClick={startAll}
                            disabled={busy || !generatable.length}
                            className="gap-1.5"
                            data-testid="render-all-pptx-start-btn"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            Generate all
                        </Button>
                    </div>

                    {(
                        <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
                            {lessons.map((l) => {
                                const st = statusFor(l.id, trayJobs, l);
                                const hasContent = (l.content || "").trim().length > 0;
                                return (
                                    <div
                                        key={l.id}
                                        className="rounded-md border border-border bg-card px-3 py-2 flex items-center gap-3"
                                        data-testid={`pptx-lesson-row-${l.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{l.title}</div>
                                            {!hasContent ? (
                                                <div className="text-xs text-muted-foreground italic">No content yet.</div>
                                            ) : st.kind === "ready" ? (
                                                <div className="text-xs text-primary flex items-center gap-1.5">
                                                    <CheckCircle2 className="h-3 w-3" />
                                                    {l.pptx_slide_count || "—"} slides ready
                                                </div>
                                            ) : st.kind === "running" ? (
                                                <div className="text-xs text-amber-800 flex items-center gap-1.5">
                                                    <Loader2 className="h-3 w-3 animate-spin" />
                                                    {st.job?.stage === "planning" ? "Planning slides…"
                                                        : st.job?.stage === "rendering" ? "Building deck…"
                                                        : st.job?.stage === "uploading" ? "Uploading…"
                                                        : "Queued…"}
                                                </div>
                                            ) : (
                                                <div className="text-xs text-muted-foreground">Not generated</div>
                                            )}
                                            {st.kind === "running" ? (
                                                <Progress
                                                    value={Math.max(5, Math.round(st.job?.percent || 5))}
                                                    className="mt-1 h-1"
                                                />
                                            ) : null}
                                        </div>
                                        {st.kind === "ready" && l.pptx_url ? (
                                            <a href={l.pptx_url} target="_blank" rel="noopener noreferrer">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="gap-1.5"
                                                    data-testid={`pptx-download-${l.id}`}
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                    Download
                                                </Button>
                                            </a>
                                        ) : null}
                                        <Button
                                            size="sm"
                                            variant={st.kind === "ready" ? "ghost" : "outline"}
                                            onClick={() => genOne(l)}
                                            disabled={st.kind === "running" || !hasContent}
                                            className="gap-1.5"
                                            data-testid={`pptx-gen-one-${l.id}`}
                                        >
                                            {st.kind === "ready" ? "Regenerate" : "Generate"}
                                        </Button>
                                    </div>
                                );
                            })}
                            {!lessons.length ? (
                                <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
                                    <AlertCircle className="h-5 w-5" />
                                    No lessons in this chapter yet.
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
